import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { ExpandLess, ExpandMore, Search as SearchIcon } from '@material-ui/icons';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { comoLista } from '../../utils/selecaoMultipla';

/**
 * Os filhos de um serviço.
 *
 * Aceita as DUAS chaves de propósito. O `GET /services` passou a devolver a
 * árvore inteira em `subservices_tree` (a importação do Movidesk trouxe quatro
 * níveis, e com `subservices` sozinho 123 serviços não chegavam na tela de
 * configuração) -- e, ao trocar, `subservices` sumiu do payload.
 *
 * Este seletor lia só `subservices`. O efeito em produção foi silencioso e
 * feio: o operador abria o chamado e via só os 73 serviços de primeiro nível,
 * sem nenhum subserviço. Nada de erro, nada no log -- só uma lista curta
 * demais que ninguém liga a uma mudança feita em outra tela.
 */
const filhosDe = (svc) => svc?.subservices_tree || svc?.subservices || [];

/** A árvore achatada, em qualquer profundidade. */
const achatar = (lista) => (lista || []).flatMap((s) => [s, ...achatar(filhosDe(s))]);

const isServiceEligible = (svc, isInternal) => {
  if (!svc || svc.is_enabled === false) return false;
  if (isInternal === null || isInternal === undefined) return true;
  return isInternal ? svc.internal_available !== false : svc.public_available !== false;
};

/**
 * O seletor de serviço, em árvore.
 *
 * Nasceu de escolha única (é assim que o chamado guarda o serviço) e ganhou
 * `multiple` para a Busca Avançada, onde a Gisele pediu marcar mais de um
 * ("não precisar criar vários Kanbans só para mudar a combinação"). O padrão
 * continua sendo escolha única: as outras cinco telas que usam este
 * componente não mudam de comportamento por isto existir.
 *
 * Com `multiple`:
 *  - `value` é uma lista (mas um escalar antigo ainda funciona -- é o que volta
 *    do `localStorage` de quem usava a busca antes desta mudança);
 *  - clicar ALTERNA a opção e o popover FICA ABERTO. Fechar a cada clique
 *    transformaria "marcar quatro serviços" em quatro idas e voltas;
 *  - `onChange` recebe a lista inteira, sempre.
 */
const ServicePicker = ({
  services = [],
  value = null,
  onChange,
  disabled = false,
  isInternal = null,
  allowClear = false,
  requireLeaf = false,
  multiple = false,
  placeholder = 'Selecione um serviço',
  emptyLabel = 'Todos os serviços',
  popoverWidth = 360,
}) => {
  const { theme } = useContext(ThemeContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedParents, setExpandedParents] = useState({});

  /** A árvore inteira, achatada -- inclusive o que não pode ser escolhido. */
  const todosFlat = useMemo(() => achatar(services), [services]);

  const parentById = useMemo(() => {
    const map = {};
    todosFlat.forEach((s) => {
      if (s?.id != null) map[String(s.id)] = s;
    });
    return map;
  }, [todosFlat]);

  const eligibleFlat = useMemo(
    () => todosFlat.filter((svc) => isServiceEligible(svc, isInternal)),
    [todosFlat, isInternal]
  );

  /**
   * O serviço ATUAL do chamado, procurado entre TODOS -- e não só entre os
   * que podem ser escolhidos.
   *
   * Um serviço desabilitado (ou indisponível para a visibilidade do chamado)
   * sai da lista de escolha, que é o comportamento desejado. Mas se ele JÁ é o
   * valor do chamado, procurá-lo só entre os elegíveis devolve nada, e o campo
   * aparece VAZIO -- como se ninguém tivesse preenchido.
   *
   * É o caso do "34.8 - Tickets Whatsapp", desabilitado de propósito porque só
   * a integração o usa: o chamado tem o serviço, e a tela dizia que não.
   * Mesma família do responsável inativo que sumia do chamado.
   */
  /**
   * Os ids marcados, como TEXTO.
   *
   * Uma lista só, para os dois modos: na escolha única ela tem zero ou um
   * item. Manter dois caminhos ("o valor" e "a lista") seria duas fontes para
   * a mesma pergunta dentro do mesmo componente.
   */
  const escolhidos = useMemo(() => comoLista(value).map(String), [value]);

  const selectedSvc = useMemo(
    () =>
      (escolhidos.length === 1
        ? todosFlat.find((svc) => String(svc?.id) === escolhidos[0]) || null
        : null),
    [todosFlat, escolhidos]
  );

  const estaMarcado = (id) => escolhidos.indexOf(String(id)) > -1;

  /** O caminho até o serviço, subindo pelos pais. */
  const getLabel = (svc) => {
    if (!svc) return '';

    const partes = [svc.name];
    let atual = svc;
    // Teto de profundidade: um `parent_id` que aponte para si mesmo (ou um
    // ciclo) travaria a tela em vez de mostrar um rótulo estranho.
    for (let i = 0; i < 10 && atual?.parent_id; i++) {
      const pai = parentById[String(atual.parent_id)];
      if (!pai) break;
      partes.unshift(pai.name);
      atual = pai;
    }

    return partes.join(' → ');
  };

  /*
   * Com vários marcados o caminho completo de cada um não cabe no botão --
   * "Suporte → Instalação, Comercial → Proposta → Revisão" vira reticências
   * antes do segundo nome. Quem precisa ver o que está marcado abre a lista,
   * onde cada item tem a sua caixa.
   */
  const selectedLabel = escolhidos.length > 1
    ? `${escolhidos.length} serviços selecionados`
    : (selectedSvc ? getLabel(selectedSvc) : '');

  /*
   * Abre os PAIS do que está marcado, para a escolha ficar à vista.
   *
   * Só quem TEM pai. Antes um serviço de primeiro nível abria a si mesmo
   * (`svc.parent_id ?? svc.id`), o que não revela nada -- ele já estava
   * visível -- e, com vários marcados, empurra a lista para baixo entre um
   * clique e o seguinte: marcar o segundo serviço acertava outro item.
   */
  useEffect(() => {
    if (escolhidos.length === 0) return;

    const abrir = {};

    escolhidos.forEach((id) => {
      const svc = todosFlat.find((s) => String(s.id) === id);
      if (!svc) return;
      const parentId = svc.parent_id;
      if (parentId == null) return;
      abrir[String(parentId)] = true;
    });

    if (Object.keys(abrir).length === 0) return;

    setExpandedParents((prev) => ({ ...prev, ...abrir }));
  }, [escolhidos, todosFlat]);

  const term = (searchTerm || '').trim().toLowerCase();
  const matchesTerm = (text) => {
    if (!term) return true;
    return String(text || '').toLowerCase().includes(term);
  };

  const eligibleParents = useMemo(
    () =>
      (services || [])
        .filter((s) => s?.parent_id == null)
        .filter((s) => isServiceEligible(s, isInternal)),
    [services, isInternal]
  );

  const eligibleParentsFiltered = useMemo(() => {
    return eligibleParents.filter((parent) => {
      const parentOk = matchesTerm(parent.name);
      const subsEligible = filhosDe(parent).filter((sub) => isServiceEligible(sub, isInternal));
      const subOk = subsEligible.some((sub) => matchesTerm(sub.name));
      return parentOk || subOk;
    });
  }, [eligibleParents, term, isInternal]);

  const closePopover = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const selectServiceId = (nextId) => {
    if (!multiple) {
      onChange?.(nextId);
      closePopover();
      return;
    }

    // "Todos os serviços" zera a lista inteira -- e, diferente da escolha
    // única, não fecha: a pessoa pode estar recomeçando a seleção.
    if (nextId === '' || nextId == null) {
      onChange?.([]);
      return;
    }

    const alvo = String(nextId);
    const restantes = escolhidos.filter((id) => id !== alvo);
    const proximos = restantes.length === escolhidos.length
      ? escolhidos.concat([alvo])
      : restantes;

    onChange?.(proximos.map(Number));
  };

  const displayText =
    selectedLabel || (allowClear && escolhidos.length === 0 ? emptyLabel : placeholder);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        fullWidth
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.default,
          '&:hover': { borderColor: theme.palette.primary.main },
        }}
        endIcon={anchorEl ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      >
        <span
          style={{
            fontWeight: 400,
            color: selectedLabel ? theme.palette.text.primary : theme.palette.text.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayText}
        </span>
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: Math.min(popoverWidth, anchorEl?.offsetWidth || popoverWidth),
              minWidth: 280,
              maxWidth: '96vw',
              maxHeight: 420,
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar serviço..."
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 4 }} />,
            }}
          />

          {allowClear && (
            <Button
              size="small"
              fullWidth
              variant={escolhidos.length === 0 ? 'contained' : 'text'}
              color={escolhidos.length === 0 ? 'primary' : 'inherit'}
              onClick={() => selectServiceId('')}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                mb: 1,
                fontWeight: escolhidos.length === 0 ? 600 : 400,
              }}
            >
              {emptyLabel}
            </Button>
          )}

          {eligibleParentsFiltered.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, p: 1 }}>
              Nenhum serviço disponível
            </Typography>
          ) : (
            eligibleParentsFiltered.map((parent) => {
              const parentKey = String(parent.id);
              const subsEligible = filhosDe(parent).filter((sub) =>
                isServiceEligible(sub, isInternal)
              );
              // Durante a busca, mostra só os subserviços que casam com o termo
              // (a menos que o próprio pai case, aí mostra todos). Sem busca, mostra todos.
              const subsToShow = term
                ? subsEligible.filter((sub) => matchesTerm(sub.name) || matchesTerm(parent.name))
                : subsEligible;
              // Com um termo de busca ativo, o pai é sempre expandido para revelar o que casou.
              const expanded = term ? true : Boolean(expandedParents[parentKey]);
              const parentSelected = estaMarcado(parent.id);
              // O pai só é escolhível quando `requireLeaf` não o bloqueia; a
              // caixa de marcar segue a mesma regra do clique no nome, senão
              // ela apareceria marcável e não marcaria nada.
              const paiEscolhivel = !(requireLeaf && subsEligible.length > 0);

              return (
                <Box key={parent.id} sx={{ mb: 0.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 1,
                      px: 0.5,
                      py: 0.25,
                    }}
                  >
                    <IconButton
                      size="small"
                      disabled={!subsEligible.length}
                      onClick={() => {
                        setExpandedParents((prev) => ({
                          ...prev,
                          [parentKey]: !prev[parentKey],
                        }));
                      }}
                      sx={{ mr: 0 }}
                    >
                      {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                    {multiple && paiEscolhivel && (
                      <Checkbox
                        size="small"
                        checked={parentSelected}
                        onChange={() => selectServiceId(parent.id)}
                        sx={{ p: 0.25 }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      title={requireLeaf && subsEligible.length > 0 ? 'Selecione um subserviço' : undefined}
                      sx={{
                        fontWeight: parentSelected ? 600 : 400,
                        color: parentSelected
                          ? theme.palette.primary.main
                          : (requireLeaf && subsEligible.length > 0 ? theme.palette.text.secondary : theme.palette.text.primary),
                        cursor: 'pointer',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        // Com requireLeaf, serviço pai com subserviços não é
                        // selecionável: força a escolha de um subserviço (só expande).
                        if (requireLeaf && subsEligible.length > 0) {
                          setExpandedParents((prev) => ({ ...prev, [parentKey]: !prev[parentKey] }));
                        } else {
                          selectServiceId(parent.id);
                        }
                      }}
                    >
                      {parent.name}
                    </Typography>
                  </Box>

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 6, pr: 1, pb: 0.5 }}>
                      {subsToShow.length === 0 ? (
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Nenhum subserviço disponível
                        </Typography>
                      ) : (
                        subsToShow.map((sub) => {
                          const subSelected = estaMarcado(sub.id);
                          return (
                            <Button
                              key={sub.id}
                              size="small"
                              /*
                               * Com várias marcadas, `contained` pinta meia
                               * lista de azul e o que está marcado deixa de
                               * saltar. Quem diz o que está marcado é a caixa.
                               */
                              variant={subSelected && !multiple ? 'contained' : 'text'}
                              color={subSelected && !multiple ? 'primary' : 'inherit'}
                              onClick={() => selectServiceId(sub.id)}
                              sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                borderRadius: 1,
                                mb: 0.25,
                                fontWeight: subSelected && multiple ? 600 : 400,
                                '&:hover': {
                                  backgroundColor: subSelected && !multiple ? undefined : theme.palette.action.hover,
                                },
                              }}
                            >
                              {/*
                                * `readOnly`: quem alterna é o clique no botão
                                * inteiro, que é a área maior. Sem isto, o
                                * React avisa "checked sem onChange" a cada
                                * render -- e aviso de console que não é
                                * defeito é o que faz o defeito passar
                                * despercebido.
                                */}
                              {multiple && (
                                <Checkbox size="small" checked={subSelected} readOnly sx={{ p: 0.25, mr: 0.5 }} />
                              )}
                              {sub.name}
                            </Button>
                          );
                        })
                      )}
                    </Box>
                  </Collapse>

                  <Divider sx={{ mt: 0.5 }} />
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ServicePicker;
